import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LiquidacionService } from 'src/app/services/liquidacion.service';
import { PdfmakeService } from 'src/app/services/pdfmake.service';
import { AuthService } from 'src/app/services/auth.service';
import { Role } from 'src/app/models/role.models';
import {
  ILiquidacion,
  ILiquidacionResponse,
  LiquidacionDetalle,
} from '../../models/liquidacion';
import {
  ILiquidacionEditableValues,
  LiquidacionStatus,
  RendicionSolicitudPago,
} from '../../models/extras';
import Swal from 'sweetalert2';
import { animate, style, transition, trigger } from '@angular/animations';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

@Component({
  templateUrl: './detalle.component.html',
  styleUrls: ['./detalle.component.css'],
  animations: [
    trigger('onOff', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(400),
      ]),
      transition(':leave', [
        animate(400, style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class DetalleLiquidacionComponent implements OnInit {
  // Utilities
  public loading: boolean = true;
  public loadingDetalles: boolean = true;
  public isEditMode: boolean = false;
  public hasChanges: boolean = false;
  public editionForm: UntypedFormGroup;
  public conceptosForm: UntypedFormGroup;
  private originalValues: any = {};
  readonly skeletonRows = Array(5);

  // Permissions
  public canEditLiquidacion: boolean = false;

  // Data Objects
  liquidacion: ILiquidacion;
  detalles: LiquidacionDetalle[] = [];
  costosVariablesGuardados: { descripcion: string; importe: number }[] = [];
  conceptosLiquidacion: { descripcion: string; importe: number }[] = [];

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _Liquidacion: LiquidacionService,
    private formBuilder: UntypedFormBuilder,
    private pdfmakeService: PdfmakeService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Verificar permisos de edición de liquidación
    this.canEditLiquidacion = this.authService.hasRole(Role.ROLE_ADMIN_LIQUIDACION);

    this.initConceptosForm();
    this.getLiquidacionData();
  }

  /**
   * Inicializa el formulario de conceptos de liquidacion
   */
  initConceptosForm() {
    this.conceptosForm = this.formBuilder.group({
      descripcion: ['', Validators.required],
      importe: ['', [Validators.required]],
    });
  }

  /**
   * Genera el formGroup con los valores de la liquidacion
   */
  makeFormGroup(properties: ILiquidacionEditableValues | {}) {
    this.editionForm = this.formBuilder.group({});
    this.originalValues = {};

    for (const property in properties) {
      if (properties.hasOwnProperty(property)) {
        const value = properties[property] / 100;
        this.originalValues[property] = value;
        const validators: ((control: AbstractControl) => ValidationErrors)[] = [
          Validators.required,
        ];
        this.editionForm.addControl(
          property,
          this.formBuilder.control(value, validators)
        );
      }
    }

    this.editionForm.valueChanges.subscribe(() => {
      const current = this.editionForm.value;
      this.hasChanges = Object.keys(this.originalValues).some(
        (key) => +current[key] !== +this.originalValues[key]
      );
    });
  }

  getLiquidacionData() {
    const id = +this._route.snapshot.paramMap.get('id');
    this._Liquidacion.getByID(id).subscribe({
      next: this.getDataSuccessHandler.bind(this),
      error: this.errorHandler.bind(this),
    });
  }

  getExtraData(liquidacion: ILiquidacion) {
    this._Liquidacion.getDetailByID(liquidacion.id).subscribe({
      next: this.getDetailSuccessHandler.bind(this),
      error: this.errorHandler.bind(this),
    });
  }

  getDetailSuccessHandler(response: LiquidacionDetalle[]) {
    this.detalles = response ?? [];
    this.loadingDetalles = false;
  }

  getDataSuccessHandler(response: ILiquidacionResponse) {
    this.liquidacion = response.content[0] as ILiquidacion;
    this.loading = false;

    const {
      importe_contracargo,
      importe_comision,
      importe_iibb_misiones,
      importe_iva,
      importe_liquidado,
      importe_ret_ganancia,
      importe_ret_iva,
      importe_sirtac,
      importe_total,
      importe_gasto_comision,
    } = this.liquidacion;

    this.makeFormGroup({
      importe_total,
      importe_liquidado,
      importe_comision,
      importe_gasto_comision,
      importe_contracargo,
      importe_sirtac,
      importe_iibb_misiones,
      importe_iva,
      importe_ret_iva,
      importe_ret_ganancia,
    });

    this.getExtraData(this.liquidacion);
    this.loadCostosVariables(this.liquidacion);
  }

  /**
   * Carga los costos variables guardados del servidor
   */
  loadCostosVariables(liquidacion: ILiquidacion) {
    if (liquidacion?.cliente?.id) {
      this._Liquidacion.refreshLiquidacionesPorCliente(liquidacion.id, liquidacion.cliente.id)
        .then((response: any) => {
          if (response?.costosVariables && Array.isArray(response.costosVariables)) {
            this.costosVariablesGuardados = response.costosVariables.map(c => ({
              descripcion: c.descripcion,
              importe: c.importe,
            }));
            // También guardar en liquidacion para que estén disponibles en el PDF
            this.liquidacion.costosVariables = response.costosVariables;
          }
        })
        .catch(() => {
          console.error('Error al cargar costos variables');
        });
    }
  }

  private showToast(icon: 'success' | 'error' | 'warning' | 'info', title: string) {
    Swal.fire({
      toast: true,
      position: 'bottom-end',
      icon,
      title,
      showConfirmButton: false,
      timer: 4000,
      timerProgressBar: true,
      customClass: { popup: 'helitoast' },
    });
  }

  errorHandler(error) {
    this.loadingDetalles = false;
    this.showToast('error', 'Ocurrió un error. COD: ' + error?.error);
  }

  openForm() {
    this.isEditMode = true;
  }

  cancelEdit() {
    this.editionForm.patchValue(this.originalValues);
    this.isEditMode = false;
    this.hasChanges = false;
  }

  submitImportesForm(values: ILiquidacionEditableValues) {
    const id = +this._route.snapshot.paramMap.get('id');
    const multipliedValues = {} as ILiquidacionEditableValues;

    const hasImporteChanges = Object.keys(this.originalValues).some(
      (key) => +values[key] !== +this.originalValues[key]
    );

    if (!this.editionForm.invalid) {
      if (hasImporteChanges) {
        for (const property in values) {
          if (values.hasOwnProperty(property)) {
            const value = values[property];
            multipliedValues[property] = typeof value === 'number' ? value * 100 : value;
          }
        }

        this._Liquidacion.updateLiquidacionValues({ ...multipliedValues, id }).subscribe({
          next: () => {
            this.showToast('success', 'Datos modificados exitosamente');
            this.submitConceptosIfExist();
            this.isEditMode = false;
            this.hasChanges = false;
            this.getLiquidacionData();
          },
          error: this.errorHandler.bind(this),
        });
      } else if (this.conceptosLiquidacion.length > 0) {
        this.submitConceptosIfExist();
        this.isEditMode = false;
        this.hasChanges = false;
      }
    } else {
      this.showToast('warning', 'Campos incompletos. Rellenar con "0" si no aplica.');
    }
  }

  /**
   * Agrega una nueva línea de concepto de liquidacion
   */
  agregarConcepto() {
    if (this.conceptosForm.valid) {
      const { descripcion, importe } = this.conceptosForm.value;
      this.conceptosLiquidacion.push({
        descripcion: descripcion.trim(),
        importe: typeof importe === 'number' ? importe * 100 : parseInt(importe) * 100,
      });
      this.conceptosForm.reset();
      this.hasChanges = true;
    }
  }

  /**
   * Elimina una línea de concepto
   */
  eliminarConcepto(index: number) {
    this.conceptosLiquidacion.splice(index, 1);
    if (this.conceptosLiquidacion.length === 0 && !Object.keys(this.originalValues).some(
      (key) => +this.editionForm.value[key] !== +this.originalValues[key]
    )) {
      this.hasChanges = false;
    }
  }

  /**
   * Envía los conceptos de liquidacion al servidor
   */
  submitConceptosIfExist() {
    if (this.conceptosLiquidacion.length > 0) {
      const id = +this._route.snapshot.paramMap.get('id');
      const clienteId = this.liquidacion?.cliente?.id;
      const payload = this.conceptosLiquidacion.map(c => ({
        descripcion: c.descripcion,
        importe: c.importe,
      }));

      this._Liquidacion.saveConceptosVariables(id, payload).subscribe({
        next: () => {
          this.showToast('success', 'Conceptos de liquidacion guardados exitosamente');
          this.conceptosLiquidacion = [];
          this.conceptosForm.reset();

          // Refrescar listado de liquidaciones por cliente
          if (clienteId) {
            this._Liquidacion.refreshLiquidacionesPorCliente(id, clienteId)
              .then((response: any) => {
                // Cargar los costos variables guardados desde la respuesta
                if (response?.costosVariables && Array.isArray(response.costosVariables)) {
                  this.costosVariablesGuardados = response.costosVariables.map(c => ({
                    descripcion: c.descripcion,
                    importe: c.importe,
                  }));
                  // También actualizar en liquidacion para que estén disponibles en el PDF
                  this.liquidacion.costosVariables = response.costosVariables;
                }
              })
              .catch(() => {
                console.error('Error al refrescar liquidaciones');
              });
          }
        },
        error: (err) => {
          if (err?.error?.message === 'COST_AMOUNT_EXCEEDS_GROSS_AMOUNT') {
            this.showToast('error', 'El importe de costos variables supera el monto bruto de la liquidación');
          } else {
            this.showToast('error', 'Error al guardar conceptos de liquidacion');
          }
        },
      });
    }
  }

  makePDFMake(liquidacionObject: ILiquidacion) {
    this.pdfmakeService.generateLiquidacionPDF(liquidacionObject);
  }

  makeExcel(liqui: ILiquidacion) {
    this._Liquidacion.exportarExcel(liqui);
  }

  handleLiqStatus(status: LiquidacionStatus): LiquidacionActionButton {
    const statusArr: LiquidacionActionButton[] = [
      { id: 'GENERADA', title: 'Generado', action: this.changeStatusProcess.bind(this) },
      { id: 'VERIFICADA', title: 'Generar verificacion', action: this.changeStatusProcess.bind(this) },
      { id: 'APROBADA', title: 'Generar aprobacion', action: this.changeStatusProcess.bind(this) },
      { id: 'REVISION', title: 'Solicitar revision', action: this.changeStatusProcess.bind(this) },
      { id: 'REAPROBAR', title: 'Aprobar revisión', action: this.changeStatusProcess.bind(this) },
    ];
    const actualIndex = statusArr.findIndex((s) => s.id === status);
    return statusArr[actualIndex + 1] ?? statusArr[actualIndex];
  }

  changeStatusProcess(id: number | string, status: LiquidacionStatus) {
    this._Liquidacion.changeProcessStatus(id, status).subscribe({
      next: this.successLiqProcess.bind(this),
      error: this.errorHandler.bind(this),
    });
  }

  successLiqProcess() {
    this.showToast('success', 'Proceso exitoso');
    this.getLiquidacionData();
  }

  getTotalDetalles(campo: keyof LiquidacionDetalle): number {
    return this.detalles.reduce((acc, d) => acc + ((d[campo] as number) ?? 0), 0) / 100;
  }

  getTotalRendiciones(campo: keyof RendicionSolicitudPago): number {
    return (this.liquidacion?.rendicion_solicitud_pago ?? [])
      .reduce((acc, r) => acc + ((r[campo] as number) ?? 0), 0) / 100;
  }

  getTotalRendicionesCantidad(): number {
    return (this.liquidacion?.rendicion_solicitud_pago ?? [])
      .reduce((acc, r) => acc + (r.cantidad_solicitud ?? 0), 0);
  }

  volver() {
    this._router.navigate(['dashboard/liquidacion']);
  }
}

interface LiquidacionActionButton {
  id: LiquidacionStatus;
  title: string;
  action: (id: number | string) => void;
}
